import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatus("timeout");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("failed");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="checkout-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="w-14 h-14 text-[#FF5C28] animate-spin mx-auto mb-6" />
          <h1 className="font-display font-black text-3xl mb-2">Confirming your payment…</h1>
          <p className="text-gray-500">Hang tight, this only takes a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
          <h1 className="font-display font-black text-3xl mb-2" data-testid="checkout-success-msg">Thank you! Order confirmed.</h1>
          <p className="text-gray-500 mb-8">A confirmation is on its way. Your cat is in good paws. 🐾</p>
          <div className="flex gap-3 justify-center">
            <Link to="/account" className="rounded-full bg-[#0F0F0F] text-white font-bold px-6 py-3 hover:bg-[#FF5C28] transition-colors">View Orders</Link>
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-[#FF5C28] text-white font-bold px-6 py-3 hover:bg-[#E04D1E] transition-colors">Keep Shopping <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="font-display font-black text-3xl mb-2">Payment not confirmed</h1>
          <p className="text-gray-500 mb-8">{status === "timeout" ? "We couldn't confirm in time. Check your email or account shortly." : "Something went wrong. You have not been charged."}</p>
          <Link to="/cart" className="rounded-full bg-[#FF5C28] text-white font-bold px-6 py-3 hover:bg-[#E04D1E] transition-colors">Back to Cart</Link>
        </>
      )}
    </div>
  );
}
